import React, { useState, useRef, forwardRef, useEffect } from 'react'
import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import useControllableState from '../hooks/useControllableState'
import useMergedRef from '../hooks/useMergeRef'
import Calendar from './Calendar'
import BasePicker from './BasePicker'
import { useConfig } from '../ConfigProvider'
import capitalize from '../utils/capitalize'

dayjs.extend(customParseFormat)

const DEFAULT_INPUT_FORMAT = 'DD/MM/YYYY'

const NumberDatePicker = forwardRef((props, ref) => {
    const {
        className,
        clearable,
        clearButton,
        closePickerOnChange,
        dateViewCount,
        dayClassName,
        dayStyle,
        defaultMonth,
        defaultOpen,
        defaultValue,
        defaultView,
        disabled,
        disableDate,
        enableHeaderLabel,
        disableOutOfMonth,
        firstDayOfWeek,
        hideOutOfMonthDates,
        hideWeekdays,
        inputFormat,
        inputPrefix,
        inputSuffix,
        labelFormat,
        locale,
        maxDate,
        minDate,
        name,
        onBlur,
        onChange,
        onFocus,
        onDropdownClose,
        onDropdownOpen,
        openPickerOnClear,
        renderDay,
        size,
        style,
        type,
        value,
        weekendDays,
        yearLabelFormat,
        popperPlacement,
        ...rest
    } = props

    const { locale: themeLocale } = useConfig()

    const finalLocale = locale || themeLocale

    const dateFormat = inputFormat || DEFAULT_INPUT_FORMAT

    const [dropdownOpened, setDropdownOpened] = useState(defaultOpen)

    const inputRef = useRef()

    const getValidDate = (val) => {
        if (val instanceof Date) return val
        if (typeof val === 'string' && val) {
            const parsed = dayjs(val, dateFormat, finalLocale)
            if (parsed.isValid()) return parsed.toDate()
        }
        return null
    }

    const [lastValidValue, setLastValidValue] = useState(defaultValue ?? null)

    const [_value, setValue] = useControllableState({
        prop: value,
        defaultProp: defaultValue,
        onChange,
    })

    const initialParsedDate = getValidDate(_value)

    const [calendarMonth, setCalendarMonth] = useState(
        initialParsedDate || defaultMonth || new Date()
    )

    const [focused, setFocused] = useState(false)

    const [inputState, setInputState] = useState(
        initialParsedDate instanceof Date
            ? capitalize(dayjs(initialParsedDate).locale(finalLocale).format(dateFormat))
            : (typeof _value === 'string' ? _value : '')
    )

    const lastValue = useRef(inputState)

    const closeDropdown = () => {
        setDropdownOpened(false)
        onDropdownClose?.()
    }

    const openDropdown = () => {
        setDropdownOpened(true)
        onDropdownOpen?.()
    }

    useEffect(() => {
        const parsedDate = getValidDate(value)
        if (parsedDate === null && !focused) {
            setInputState('')
            lastValue.current = ''
        }

        if (parsedDate instanceof Date && !focused) {
            const formatted = capitalize(dayjs(parsedDate).locale(finalLocale).format(dateFormat))
            setInputState(formatted)
            lastValue.current = formatted
        }
    }, [value, focused, themeLocale])

    useEffect(() => {
        const parsedDefault = getValidDate(defaultValue)
        if (parsedDefault instanceof Date && inputState && !focused) {
            const formatted = capitalize(dayjs(parsedDefault).locale(finalLocale).format(dateFormat))
            setInputState(formatted)
            lastValue.current = formatted
        }
    }, [themeLocale])

    const handleValueChange = (date) => {
        setValue(date)
        const formatted = capitalize(dayjs(date).locale(finalLocale).format(dateFormat))
        setInputState(formatted)
        lastValue.current = formatted
        closePickerOnChange && closeDropdown()
        window.setTimeout(() => inputRef.current?.focus(), 0)
    }

    const handleClear = () => {
        setValue(null)
        setLastValidValue(null)
        setInputState('')
        lastValue.current = ''
        openPickerOnClear && openDropdown()
        inputRef.current?.focus()
    }

    const parseDate = (date) => dayjs(date, dateFormat, finalLocale).toDate()

    const isDateWithinRange = (dateStr) => {
        if (!dateStr) return false
        const parts = dateStr.split(/[^0-9]/)
        if (parts.length === 3) {
            const day = parseInt(parts[0], 10)
            const month = parseInt(parts[1], 10)
            const year = parseInt(parts[2], 10)
            
            if (day < 1 || day > 31) return false
            if (month < 1 || month > 12) return false
            if (year < 1900 || year > 2072) return false
            return true
        }
        return false
    }

    const setDateFromInput = () => {
        if (inputState && inputState.length === 10) {
            if (isDateWithinRange(inputState)) {
                const date = dayjs(inputState, dateFormat, finalLocale).toDate()
                if (dayjs(date).isValid()) {
                    let finalDate = date
                    if (maxDate && dayjs(finalDate).isAfter(maxDate)) {
                        finalDate = maxDate
                    }
                    if (minDate && dayjs(finalDate).isBefore(minDate)) {
                        finalDate = minDate
                    }
                    setValue(finalDate)
                    setLastValidValue(finalDate)
                    const formatted = capitalize(dayjs(finalDate).locale(finalLocale).format(dateFormat))
                    setInputState(formatted)
                    lastValue.current = formatted
                    setCalendarMonth(finalDate)
                    return
                }
            }
        }

        if (lastValidValue) {
            setValue(lastValidValue)
            const formatted = capitalize(dayjs(lastValidValue).locale(finalLocale).format(dateFormat))
            setInputState(formatted)
            lastValue.current = formatted
            setCalendarMonth(lastValidValue)
        } else {
            setValue(null)
            setInputState('')
            lastValue.current = ''
        }
    }

    const handleInputBlur = (event) => {
        typeof onBlur === 'function' && onBlur(event)
        setFocused(false)
        setDateFromInput()
    }

    const handleKeyDown = (event) => {
        if (event.key === 'Enter') {
            closeDropdown()
            setDateFromInput()
        }
    }

    const handleInputFocus = (event) => {
        typeof onFocus === 'function' && onFocus(event)
        setFocused(true)
    }

    const formatInput = (rawVal) => {
        const clean = rawVal.replace(/\D/g, '').slice(0, 8)
        let result = ''
        
        if (clean.length > 0) {
            result += clean.slice(0, 2)
            if (clean.length > 2) {
                result += '/' + clean.slice(2, 4)
                if (clean.length > 4) {
                    result += '/' + clean.slice(4, 8)
                }
            }
        }
        return result
    }

    const handleChange = (event) => {
        openDropdown()
        let val = event.target.value
        
        if (val.length < lastValue.current.length) {
            const lastCharDeleted = lastValue.current.slice(val.length)
            if (lastCharDeleted.match(/[^0-9]/)) {
                const temp = val.replace(/[^0-9]+$/, '')
                val = temp.slice(0, -1)
            }
        }
        
        const formatted = formatInput(val)
        setInputState(formatted)
        lastValue.current = formatted
        
        if (formatted.length === 10) {
            if (isDateWithinRange(formatted)) {
                const date = dayjs(formatted, dateFormat, finalLocale).toDate()
                if (dayjs(date).isValid()) {
                    setValue(date)
                    setLastValidValue(date)
                    setCalendarMonth(date)
                }
            }
        }
    }

    return (
        <BasePicker
            inputtable={true}
            dropdownOpened={dropdownOpened}
            setDropdownOpened={setDropdownOpened}
            ref={useMergedRef(ref, inputRef)}
            size={size}
            style={style}
            className={className}
            onChange={handleChange}
            onBlur={handleInputBlur}
            onFocus={handleInputFocus}
            onKeyDown={handleKeyDown}
            name={name}
            inputLabel={inputState}
            clearable={
                type === 'date' ? false : clearable && !!_value && !disabled
            }
            clearButton={clearButton}
            onClear={handleClear}
            disabled={disabled}
            onDropdownClose={onDropdownClose}
            onDropdownOpen={onDropdownOpen}
            type={type}
            inputPrefix={inputPrefix}
            inputSuffix={inputSuffix}
            placement={popperPlacement}
            {...rest}
        >
            <Calendar
                locale={finalLocale}
                month={calendarMonth}
                defaultMonth={
                    defaultMonth ||
                    (_value instanceof Date ? _value : new Date())
                }
                onMonthChange={setCalendarMonth}
                value={
                    _value instanceof Date
                        ? _value
                        : _value && dayjs(_value).toDate()
                }
                onChange={handleValueChange}
                labelFormat={labelFormat}
                dayClassName={dayClassName}
                dayStyle={dayStyle}
                disableOutOfMonth={disableOutOfMonth}
                minDate={minDate}
                maxDate={maxDate}
                disableDate={disableDate}
                firstDayOfWeek={firstDayOfWeek}
                preventFocus={true}
                dateViewCount={dateViewCount}
                enableHeaderLabel={enableHeaderLabel}
                defaultView={defaultView}
                hideOutOfMonthDates={hideOutOfMonthDates}
                hideWeekdays={hideWeekdays}
                renderDay={renderDay}
                weekendDays={weekendDays}
                yearLabelFormat={yearLabelFormat}
            />
        </BasePicker>
    )
})

NumberDatePicker.defaultProps = {
    closePickerOnChange: true,
    labelFormat: {
        month: 'MMM',
        year: 'YYYY',
    },
    defaultOpen: false,
    name: 'date',
    clearable: true,
    disabled: false,
    firstDayOfWeek: 'monday',
    openPickerOnClear: false,
}

export default NumberDatePicker
